import React, { useState, useEffect } from 'react'
import PageHeader from '@components/PageHeader'
import './index.scss'

import { useNavigate, useSearchParams, Navigate } from 'react-router-dom'
import { useAuth } from '@auth/AuthProvider'
import EventsController, { ExternalEvent } from '@data/controller/EventsController'
import EventCard from '@pages/Events/EventCard'
import LoadingIndicator from '@components/LoadingIndicator'

export default function ExternalImportPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [externalLoading, setExternalLoading] = useState(false);
  const [externalError, setExternalError] = useState<string | null>(null);
  const [externalEvents, setExternalEvents] = useState<ExternalEvent[] | null>(null);

  const provider = searchParams.get('provider');
  let providerName = '';
  if (provider === 'PLANNING_CENTER') {
    providerName = 'Planning Center';
  }

  // If provider is missing/unsupported, declaratively redirect back to the chooser.
  if (!provider || provider !== 'PLANNING_CENTER') {
    return <Navigate to="/events/create" replace />;
  }

  // Only fetch external events when we have a valid provider and church context.
  useEffect(() => {
    if (!provider || provider !== 'PLANNING_CENTER') return;
    let isMounted = true;

    const performFetch = async () => {
      if (!auth.churchId) return;
      setExternalLoading(true);
      setExternalError(null);
      setExternalEvents(null);

      try {
        const resp = await EventsController.fetchExternalImportOptions(auth.churchId, provider as any, auth);
        if (!isMounted) return;

        if (resp.error) {
          setExternalError(resp.error);
        } else if (resp.body) {
          setExternalEvents(resp.body.externalEvents);
        }
      } catch (err: any) {
        setExternalError(err?.message || 'Failed to load external events');
      } finally {
        setExternalLoading(false);
      }
    };

    performFetch();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line
  }, [provider, auth.churchId]);

  const navigateToCreateForEvent = (ev: ExternalEvent) => {
    const params = new URLSearchParams();
    if (ev.name) params.set('title', ev.name);
    if (ev.description) params.set('description', ev.description as string);
    if (ev.registrationURL) params.set('registrationURL', ev.registrationURL as string);
    if (ev.thumbnailImageURL) params.set('thumbnailURL', ev.thumbnailImageURL as string);
    params.set('externalEventID', ev.externalID);
    params.set('provider', provider as string);

    const search = params.toString();
    navigate({ pathname: '/events/create/details', search: search ? `?${search}` : '' });
  }

  return <div className='create-event-external'>
    <PageHeader title={`Select ${providerName} Event`} showBackButton={true} backLocation="/events/create" />

    <div className='external-list'>
      {externalLoading ? <LoadingIndicator /> : null}
      {externalError ? <p className='error'>{externalError}</p> : null}
      {externalEvents ? <>
        {externalEvents.length === 0 ? <p>No events available to import.</p> : null}
        <div className='cards'>
          {externalEvents.map((ev) => <EventCard key={ev.externalID}
            title={ev.name}
            thumbnailURL={ev.thumbnailImageURL}
            dateInfo={null}
            onClick={() => {
              navigateToCreateForEvent(ev);
            }} />)}
        </div>
      </> : null}
    </div>
  </div>
}
